import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import {
  Badge, Button, Card, DataTable, DateInput, Dialog, Field, Input, Pagination, Select, Textarea,
} from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { date, minorFromRupees, money, number, percent, rupeesFromMinor } from '../../lib/format.js';

/**
 * A code starts as a draft.
 *
 * The same rule as a package, for the same reason: a half-written coupon with
 * a missing end date is a discount with no expiry, and the gap between typing
 * it and finishing it should not be a window somebody can buy through.
 */
const EMPTY_COUPON = {
  code: '',
  description: '',
  type: 'percent',
  value: 10,
  maxDiscountMinor: null,
  audience: '',
  minSpendMinor: 0,
  startsAt: null,
  endsAt: null,
  maxRedemptions: null,
  maxRedemptionsPerUser: 1,
  status: 'draft',
};

const TYPES = [
  { value: 'percent', label: 'Percentage off' },
  { value: 'fixed', label: 'Fixed amount off' },
];

const STATUSES = [
  { value: 'draft', label: 'Draft — cannot be redeemed' },
  { value: 'published', label: 'Live — can be redeemed now' },
];

/**
 * Which catalogue it works in. The blank option is not "none" but "both",
 * matching how an absent audience is read on a credit package.
 */
const AUDIENCES = [
  { value: '', label: 'Both catalogues' },
  { value: 'customer', label: 'Owners and tenants' },
  { value: 'broker', label: 'Agencies' },
];

/** What the code takes off, in the words the row needs. */
function discountLabel(coupon) {
  if (coupon.type === 'percent') {
    const cap = coupon.maxDiscountMinor ? ` up to ${money(coupon.maxDiscountMinor)}` : '';
    return `${percent(coupon.value)}${cap}`;
  }
  return money(coupon.value);
}

/**
 * Why a code is not currently redeemable, or null if it is.
 *
 * A coupon can be published and still dead — not started, finished, or fully
 * claimed — and an admin looking at a list of live-looking rows needs that
 * said rather than inferred from two dates and a counter.
 */
function dormantReason(coupon, now = new Date()) {
  if (coupon.status !== 'published') return null;
  if (coupon.startsAt && now < new Date(coupon.startsAt)) return 'Not started';
  if (coupon.endsAt && now > new Date(coupon.endsAt)) return 'Expired';
  if (coupon.maxRedemptions != null && coupon.redeemedCount >= coupon.maxRedemptions) {
    return 'Fully claimed';
  }
  return null;
}

/**
 * Discount codes against a credit top-up.
 *
 * Paginated and searchable where the packages tab is a pair of small grids:
 * a catalogue is a handful of entries edited by hand, but codes accumulate one
 * campaign at a time and the expired ones are kept, because a buyer asking
 * what they were charged is the reason the record exists.
 *
 * Amounts are typed and shown in rupees; the ×100 happens in `lib/format.js`.
 */
export default function CouponsTab() {
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.CONFIG_MANAGE);

  const queryClient = useQueryClient();
  const list = useListQuery({
    key: 'admin-credit-coupons',
    fetcher: api.credits.coupons,
    initialFilters: { status: '', audience: '' },
    searchable: true,
  });

  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [errors, setErrors] = useState({});

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin-credit-coupons'] });

  const open = (coupon) => {
    setEditing(coupon ? { ...EMPTY_COUPON, ...coupon, audience: coupon.audience || '' } : { ...EMPTY_COUPON });
    setErrors({});
  };

  const close = () => {
    setEditing(null);
    setErrors({});
  };

  const save = useMutation({
    mutationFn: (body) =>
      editing._id ? api.credits.updateCoupon(editing._id, body) : api.credits.createCoupon(body),
    onSuccess: (saved) => {
      toast.success(`${saved.code} saved.`);
      invalidate();
      close();
    },
    onError: (error) => {
      /*
       * The API answers a bad field with `details.fields`, which is the same
       * shape this form already renders. Showing it against the input beats a
       * toast that names a field the admin then has to go and find.
       */
      const fields = error?.details?.fields;
      if (fields) setErrors(fields);
      else toast.error(error.message);
    },
  });

  const remove = useMutation({
    mutationFn: (couponId) => api.credits.removeCoupon(couponId),
    onSuccess: (result) => {
      toast.success(`${result.removed.code} deleted.`);
      invalidate();
      setRemoving(null);
    },
    onError: (error) => toast.error(error.message),
  });

  const setField = (name, value) => setEditing((current) => ({ ...current, [name]: value }));

  const submit = () => {
    const found = {};
    const value = Number(editing.value);

    if (!/^[A-Za-z0-9_-]{3,40}$/.test(String(editing.code).trim())) {
      found.code = 'Letters, numbers, hyphen and underscore only.';
    }
    if (editing.type === 'percent' && (!Number.isInteger(value) || value < 1 || value > 100)) {
      found.value = 'A percentage has to be between 1 and 100.';
    }
    if (editing.type === 'fixed' && (!Number.isInteger(value) || value < 1)) {
      found.value = 'Enter the amount this takes off.';
    }
    // The money fields are already in paise (the inputs convert on the way in).
    if (
      editing.type === 'percent' &&
      editing.maxDiscountMinor != null &&
      editing.maxDiscountMinor !== '' &&
      !(Number(editing.maxDiscountMinor) >= 100)
    ) {
      found.maxDiscountMinor = 'A cap has to be at least ₹1, or empty for none.';
    }
    if (editing.minSpendMinor != null && editing.minSpendMinor !== '' && !(Number(editing.minSpendMinor) >= 0)) {
      found.minSpendMinor = 'Minimum spend cannot be negative.';
    }
    const totalUses = String(editing.maxRedemptions ?? '').trim();
    if (totalUses !== '' && (!Number.isInteger(Number(totalUses)) || Number(totalUses) < 1)) {
      found.maxRedemptions = 'A whole number of at least 1, or empty for unlimited.';
    }
    const perUser = Number(String(editing.maxRedemptionsPerUser ?? '').trim() || NaN);
    if (!Number.isInteger(perUser) || perUser < 1) {
      found.maxRedemptionsPerUser = 'A whole number of at least 1.';
    }
    if (editing.startsAt && editing.endsAt && new Date(editing.endsAt) <= new Date(editing.startsAt)) {
      found.endsAt = 'The end has to come after the start.';
    }

    setErrors(found);
    if (Object.keys(found).length) return;

    save.mutate({
      code: String(editing.code).trim().toUpperCase(),
      description: String(editing.description || '').trim() || undefined,
      type: editing.type,
      value,
      // A cap only means something for a percentage; sending it for a fixed
      // amount would store a rule that never applies.
      maxDiscountMinor: editing.type === 'percent' ? editing.maxDiscountMinor ?? null : null,
      audience: editing.audience || null,
      minSpendMinor: Number(editing.minSpendMinor) || 0,
      startsAt: editing.startsAt || null,
      endsAt: editing.endsAt || null,
      maxRedemptions: totalUses ? Number(totalUses) : null,
      maxRedemptionsPerUser: perUser,
      status: editing.status,
    });
  };

  const columns = [
    {
      key: 'code',
      header: 'Code',
      render: (coupon) => (
        <div>
          <div className="table__primary mono">{coupon.code}</div>
          {coupon.description ? <div className="table__muted">{coupon.description}</div> : null}
        </div>
      ),
    },
    {
      key: 'discount',
      header: 'Takes off',
      render: (coupon) => (
        <div>
          <div>{discountLabel(coupon)}</div>
          {coupon.minSpendMinor ? (
            <div className="table__muted">{`On ${money(coupon.minSpendMinor)}+`}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'audience',
      header: 'Sold to',
      render: (coupon) =>
        AUDIENCES.find((entry) => entry.value === (coupon.audience || ''))?.label || '—',
    },
    {
      key: 'window',
      header: 'Runs',
      render: (coupon) =>
        !coupon.startsAt && !coupon.endsAt ? (
          <span className="muted">No end</span>
        ) : (
          <div>
            <div>{date(coupon.startsAt)}</div>
            <div className="table__muted">{`to ${date(coupon.endsAt)}`}</div>
          </div>
        ),
    },
    {
      key: 'used',
      header: 'Used',
      render: (coupon) => (
        <span>
          {number(coupon.redeemedCount || 0)}
          {coupon.maxRedemptions ? ` / ${number(coupon.maxRedemptions)}` : ''}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (coupon) => {
        const dormant = dormantReason(coupon);
        if (coupon.status !== 'published') return <Badge tone="neutral" label="Draft" />;
        if (dormant) return <Badge tone="warning" label={dormant} />;
        return <Badge tone="success" label="Live" />;
      },
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: '',
            render: (coupon) => (
              <div className="row">
                <Button variant="outline" size="sm" onClick={() => open(coupon)}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setRemoving(coupon)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const isNew = editing != null && !editing._id;

  return (
    <>
      <Card
        title="Discount codes"
        subtitle="What a code takes off a credit top-up, who can use it, and how often."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => open(null)}>
              New coupon
            </Button>
          ) : null
        }
      >
        <div className="filters">
          <Input
            placeholder="Search a code"
            value={list.search}
            onChange={(event) => list.setSearch(event.target.value)}
          />
          <div className="row">
            <Select
              value={list.filters.status}
              onChange={(event) => list.setFilter('status', event.target.value)}
            >
              <option value="">Any status</option>
              <option value="published">Live</option>
              <option value="draft">Draft</option>
            </Select>
            <Select
              value={list.filters.audience}
              onChange={(event) => list.setFilter('audience', event.target.value)}
            >
              <option value="">Any catalogue</option>
              <option value="customer">Owners and tenants</option>
              <option value="broker">Agencies</option>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          empty={{
            title: 'No codes yet',
            body: canManage
              ? 'Create one to give a campaign something to hand out. Nothing is live until you publish it.'
              : 'No discount codes have been created yet.',
          }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>

      <Dialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Delete ${removing?.code}?`}
        warning={
          removing?.redeemedCount
            ? `This code has been used ${number(removing.redeemedCount)} time${removing.redeemedCount === 1 ? '' : 's'}, so it cannot be deleted — the payments it discounted reference it for what the discount was. Set it to draft to take it out of circulation.`
            : 'Nobody has used this code, so removing it takes nothing away. Its definition is kept in the audit log.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => remove.mutate(removing._id)}
              loading={remove.isPending}
              disabled={Boolean(removing?.redeemedCount)}
            >
              Delete coupon
            </Button>
          </>
        }
      />

      <Dialog
        open={Boolean(editing)}
        onClose={close}
        title={isNew ? 'New coupon' : `Edit ${editing?.code}`}
        warning={
          editing?.status === 'published'
            ? 'Live: it can be redeemed the moment you save. Purchases already made with it keep the discount they were given. Recorded in the audit log against your account.'
            : 'A draft cannot be redeemed. Publishing is what puts it in circulation.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {isNew ? 'Create coupon' : 'Save coupon'}
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="stack">
            <div className="field-row">
              <Field label="Code" hint="Typed by the buyer. Case does not matter." error={errors.code} id="coupon-code">
                <Input
                  id="coupon-code"
                  value={editing.code}
                  error={errors.code}
                  onChange={(event) => setField('code', event.target.value.toUpperCase())}
                  placeholder="LAUNCH20"
                />
              </Field>
              <Field label="Status" id="coupon-status">
                <Select
                  id="coupon-status"
                  value={editing.status}
                  onChange={(event) => setField('status', event.target.value)}
                >
                  {STATUSES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field
              label="Description"
              hint="For this screen only. The buyer never sees it."
              error={errors.description}
              id="coupon-description"
            >
              <Textarea
                id="coupon-description"
                rows={2}
                value={editing.description || ''}
                onChange={(event) => setField('description', event.target.value)}
                placeholder="Launch week, 20% off a top-up"
              />
            </Field>

            <div className="field-row">
              <Field label="Type" id="coupon-type">
                <Select
                  id="coupon-type"
                  value={editing.type}
                  onChange={(event) => setField('type', event.target.value)}
                >
                  {TYPES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>

              {editing.type === 'percent' ? (
                <>
                  <Field label="Percentage off" error={errors.value} id="coupon-value">
                    <Input
                      id="coupon-value"
                      type="text"
                      inputMode="numeric"
                      error={errors.value}
                      value={editing.value}
                      onChange={(event) => setField('value', event.target.value)}
                    />
                  </Field>
                  <Field
                    label="Cap (₹)"
                    hint="Most it can take off. Leave empty for none."
                    error={errors.maxDiscountMinor}
                    id="coupon-cap"
                  >
                    <Input
                      id="coupon-cap"
                      type="text"
                      inputMode="numeric"
                      error={errors.maxDiscountMinor}
                      value={rupeesFromMinor(editing.maxDiscountMinor)}
                      onChange={(event) =>
                        setField('maxDiscountMinor', minorFromRupees(event.target.value))
                      }
                    />
                  </Field>
                </>
              ) : (
                <Field label="Amount off (₹)" error={errors.value} id="coupon-value">
                  <Input
                    id="coupon-value"
                    type="text"
                    inputMode="numeric"
                    error={errors.value}
                    value={rupeesFromMinor(editing.value)}
                    onChange={(event) => setField('value', minorFromRupees(event.target.value))}
                  />
                </Field>
              )}
            </div>

            <div className="field-row">
              <Field label="Sold to" hint="Which catalogue it works in." id="coupon-audience">
                <Select
                  id="coupon-audience"
                  value={editing.audience}
                  onChange={(event) => setField('audience', event.target.value)}
                >
                  {AUDIENCES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Minimum spend (₹)"
                hint="Before tax. Empty for none."
                error={errors.minSpendMinor}
                id="coupon-min"
              >
                <Input
                  id="coupon-min"
                  type="text"
                  inputMode="numeric"
                  error={errors.minSpendMinor}
                  value={rupeesFromMinor(editing.minSpendMinor)}
                  onChange={(event) => setField('minSpendMinor', minorFromRupees(event.target.value))}
                />
              </Field>
            </div>

            <div className="field-row">
              <Field label="Starts" hint="Empty means immediately." id="coupon-starts">
                <DateInput
                  id="coupon-starts"
                  value={editing.startsAt ? String(editing.startsAt).slice(0, 10) : ''}
                  onChange={(event) => setField('startsAt', event.target.value || null)}
                />
              </Field>
              <Field label="Ends" hint="Empty means no end." error={errors.endsAt} id="coupon-ends">
                <DateInput
                  id="coupon-ends"
                  error={errors.endsAt}
                  value={editing.endsAt ? String(editing.endsAt).slice(0, 10) : ''}
                  onChange={(event) => setField('endsAt', event.target.value || null)}
                />
              </Field>
            </div>

            <div className="field-row">
              <Field
                label="Total uses"
                hint="Across everyone. Empty for unlimited."
                error={errors.maxRedemptions}
                id="coupon-max"
              >
                <Input
                  id="coupon-max"
                  type="text"
                  inputMode="numeric"
                  error={errors.maxRedemptions}
                  value={editing.maxRedemptions ?? ''}
                  onChange={(event) => setField('maxRedemptions', event.target.value)}
                />
              </Field>
              <Field
                label="Uses per account"
                hint="How many times one account can use it. At least 1."
                error={errors.maxRedemptionsPerUser}
                id="coupon-max-per-user"
              >
                <Input
                  id="coupon-max-per-user"
                  type="text"
                  inputMode="numeric"
                  error={errors.maxRedemptionsPerUser}
                  value={editing.maxRedemptionsPerUser ?? ''}
                  onChange={(event) => setField('maxRedemptionsPerUser', event.target.value)}
                />
              </Field>
            </div>

            {editing._id ? (
              <p className="muted" style={{ fontSize: 12 }}>
                {`Used ${number(editing.redeemedCount || 0)} time${editing.redeemedCount === 1 ? '' : 's'} so far. That count is a record of what has happened and is not editable here.`}
              </p>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
